import { useContext } from 'react'
import { BankContext, HouseCurrency, HouseSales } from './context'

const BalanceBlock = ({ amount, symbol, header = undefined, title = undefined, className = undefined }) => {
    return (
        <div className='flex flex-col items-center gap-1' title={title}>
            {header && <header className='text-xs text-violet-500 font-bold'>{header}</header>}
            <div
                className={['flex flex-col items-center gap-1 px-4 py-2 rounded bg-white/5', className]
                    .filter(Boolean)
                    .join(' ')}
            >
                <div className='font-bold'>{amount}</div>
                <div className='text-xs text-white/50'>{symbol}</div>
            </div>
        </div>
    )
}

/** "12:04:26 UTC". Stated in UTC so nobody has to guess the zone. */
const asOfText = (iso: string | null) => (iso ? `${iso.slice(11, 19)} UTC` : '...')

/** The house account, one block per currency.
 *
 *  This used to be a single figure headed "Bank": br1's balance in its first
 *  currency. Beside "Users" it read as the house's own money, while most of it
 *  was player money parked there by freezes and held withdrawals (72% on
 *  2026-09-29, audit BANK-02). The house's own share leads, and the custody it
 *  does not own is shown beside it rather than inside it. A negative house
 *  share is shown as such: it means br1 has paid out of money it is holding.
 */
export const BankBalance = () => {
    const { house } = useContext(BankContext)
    return (
        <>
            {house.currencies.map((c: HouseCurrency) => (
                <div key={c.symbol} className='flex flex-row gap-2 items-end'>
                    <BalanceBlock
                        header='House share'
                        amount={c.house}
                        symbol={c.symbol}
                        className={c.house < 0 ? 'text-red-500' : undefined}
                        title={`br1 holds ${c.balance} ${c.symbol} in total; ${c.custody} of it is player money in custody, so the house's own share is ${c.house}.`}
                    />
                    {Number(c.custody) !== 0 && (
                        <BalanceBlock
                            header='Player money in custody'
                            amount={c.custody}
                            symbol={c.symbol}
                            className='text-amber-400'
                            title={`Frozen accounts: ${c.custody_frozen} ${c.symbol}. Withdrawals declined and frozen: ${c.custody_held} ${c.symbol}. This sits on br1 but belongs to players until it is released, returned or destroyed.`}
                        />
                    )}
                </div>
            ))}
            {/* D-113 A: revenue beside the house share, never inside it. */}
            {(house.sales ?? []).map((s: HouseSales) => (
                <BalanceBlock
                    key={`sales-${s.symbol}`}
                    header='Store + pack sales'
                    amount={s.total}
                    symbol={s.symbol}
                    title={`Lifetime sales to players, net of refunds; staff, test and fraud-tagged buyers left out. Store ${s.store}, packs ${s.packs} ${s.symbol}. A sale debits the player and credits no account, so this is not on br1 and not part of the house share.`}
                />
            ))}
        </>
    )
}

export const UsersBalance = () => {
    const { usersBalance } = useContext(BankContext)
    return (
        <>
            {usersBalance.map((b) => (
                <BalanceBlock
                    key={b.symbol}
                    amount={b.amount}
                    symbol={b.symbol}
                    header='Users'
                    title={
                        // D-097: public players, the same rule as the Dashboard.
                        b.symbol === 'AIRDROP'
                            ? 'Every public player balance (staff, test and fraud-tagged accounts excluded), expired AIRDROP excluded.'
                            : 'Every public player balance (staff, test and fraud-tagged accounts excluded). Frozen money is on br1, not here.'
                    }
                />
            ))}
        </>
    )
}

/** When the figures above were read, and a way to read them again. They are
 *  loaded once when the page opens and nothing refreshes them (screen check C4). */
export const BalanceAsOf = () => {
    const { house, usersAsOf, reloadBalance } = useContext(BankContext)
    return (
        <div className='text-xs text-white/40 flex flex-row gap-2 items-center'>
            <span>
                as of {asOfText(house.as_of)}
                {usersAsOf && house.as_of && usersAsOf.slice(11, 19) !== house.as_of.slice(11, 19) && (
                    <> (users {asOfText(usersAsOf)})</>
                )}
            </span>
            <button onClick={reloadBalance} className='hover:text-violet-500 underline'>
                refresh
            </button>
        </div>
    )
}
